// The guides hub's public pages (/guides and /guides/<slug>) for src/lib/seo/pages.ts: their titles, descriptions,
// link-preview images, sitemap dates and JSON-LD (Article with its author and publisher, BreadcrumbList, and FAQPage
// for the questions every guide shows on the page). Only listed where the guides are on (src/lib/guidesFlag.ts).

import {
  ALL_GUIDES,
  GUIDES_INDEX_SEO,
  GUIDES_PATH,
  GUIDE_SLUGS,
  getGuide,
  guideLangs,
  guidePath,
  listGuides,
  type Guide,
} from "@/content/guides";
import { authorAnchor, findAuthor, type GuideAuthor } from "@/content/guides/authors";
import { plainInline } from "@/content/guides/body";
import { LANG_TAG, type OgImage } from "./site";
import type { PublicPage, StructuredDataContext } from "./pages";

/** A guide's 1200x630 link-preview image (public/images/og/guides, made by scripts/brand-assets/guide-og.mjs). */
export const guideOgImage = (slug: string, alt: string): OgImage => ({
  path: `/images/og/guides/${slug}.jpg`,
  width: 1200,
  height: 630,
  type: "image/jpeg",
  alt,
});

export const GUIDES_HUB_OG = guideOgImage("index", "Photohawk guides to event and race photos.");

const orgRef = (origin: string) => ({ "@id": `${origin}/#organization` });
const websiteRef = (origin: string) => ({ "@type": "WebSite", "@id": `${origin}/#website`, name: "Photohawk", url: `${origin}/` });

/** schema.org for a guide's author or reviewer: a Person, or Photohawk's team as part of the Organization. */
export const authorLd = (a: GuideAuthor, origin: string): object =>
  a.kind === "person"
    ? {
        "@type": "Person",
        name: a.name,
        jobTitle: a.role,
        description: a.bio,
        url: `${origin}${GUIDES_PATH}#${authorAnchor(a.id)}`,
        worksFor: orgRef(origin),
        ...(a.sameAs?.length ? { sameAs: a.sameAs } : {}),
      }
    : {
        "@type": "Organization",
        name: a.name,
        description: a.bio,
        url: `${origin}${GUIDES_PATH}#${authorAnchor(a.id)}`,
        parentOrganization: orgRef(origin),
      };

const breadcrumbs = (origin: string, url: string, items: { name: string; url: string }[]) => ({
  "@type": "BreadcrumbList",
  "@id": `${url}#breadcrumbs`,
  itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: it.url })),
});

/** The JSON-LD nodes for one guide. */
export function guideStructuredData(g: Guide, ctx: StructuredDataContext): object[] {
  const { origin, url, image } = ctx;
  const author = findAuthor(g.author)!;
  const reviewer = findAuthor(g.reviewer);
  const nodes: object[] = [
    {
      "@type": "WebPage",
      "@id": `${url}#page`,
      url,
      name: ctx.title,
      description: ctx.description,
      inLanguage: LANG_TAG[g.lang],
      isPartOf: websiteRef(origin),
      breadcrumb: { "@id": `${url}#breadcrumbs` },
      lastReviewed: g.lastReviewed,
      // A placeholder (TODO) reviewer is never published as structured data.
      ...(reviewer && !reviewer.placeholder ? { reviewedBy: authorLd(reviewer, origin) } : {}),
    },
    {
      "@type": "Article",
      "@id": `${url}#article`,
      headline: g.title,
      description: g.description,
      inLanguage: LANG_TAG[g.lang],
      mainEntityOfPage: { "@id": `${url}#page` },
      image: { "@type": "ImageObject", url: image, width: 1200, height: 630 },
      datePublished: g.published,
      dateModified: g.updated,
      author: authorLd(author, origin),
      publisher: orgRef(origin),
      wordCount: g.words,
      about: g.primaryQuery,
    },
    breadcrumbs(origin, url, [
      { name: "Home", url: `${origin}/` },
      { name: "Guides", url: `${origin}${GUIDES_PATH}` },
      { name: g.title, url },
    ]),
  ];
  // Only for questions the page shows (GuidePage renders every one of them under "Questions people ask").
  if (g.faq.length) {
    nodes.push({
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      mainEntity: g.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: plainInline(f.a) },
      })),
    });
  }
  return nodes;
}

const latest = (dates: string[]) => dates.reduce((a, b) => (a > b ? a : b), "");

/** /guides and every guide, for publicPages() where the guides are on. */
export function guidePages(): PublicPage[] {
  const hub: PublicPage = {
    path: GUIDES_PATH,
    langs: ["en"],
    seo: () => GUIDES_INDEX_SEO,
    image: GUIDES_HUB_OG,
    type: "website",
    indexable: true,
    changefreq: "weekly",
    priority: 0.6,
    lastmod: latest(ALL_GUIDES.map((g) => g.updated)),
    structuredData: ({ origin, url, title, description }) => [
      {
        "@type": "CollectionPage",
        "@id": `${url}#page`,
        url,
        name: title,
        description,
        inLanguage: "en",
        isPartOf: websiteRef(origin),
        publisher: orgRef(origin),
        breadcrumb: { "@id": `${url}#breadcrumbs` },
        mainEntity: {
          "@type": "ItemList",
          itemListElement: listGuides("en").map((g, i) => ({ "@type": "ListItem", position: i + 1, url: `${origin}${guidePath(g.slug)}`, name: g.title })),
        },
      },
      breadcrumbs(origin, url, [
        { name: "Home", url: `${origin}/` },
        { name: "Guides", url },
      ]),
    ],
  };
  const guides = GUIDE_SLUGS.map((slug): PublicPage => {
    const en = getGuide(slug, "en")!;
    return {
      path: guidePath(slug),
      langs: guideLangs(slug),
      seo: (lang) => {
        const g = getGuide(slug, lang)!;
        return { title: g.seoTitle ?? `${g.title} | Photohawk`, description: g.description };
      },
      image: guideOgImage(slug, en.title),
      type: "article",
      indexable: true,
      changefreq: "monthly",
      priority: 0.6,
      lastmod: latest(ALL_GUIDES.filter((g) => g.slug === slug).map((g) => g.updated)),
      structuredData: (ctx) => guideStructuredData(getGuide(slug, ctx.lang)!, ctx),
    };
  });
  return [hub, ...guides];
}
